import { useEffect, useState } from "react";
import { useLocations } from "../../hooks/useLocations";
import { Button } from "@tonic/shared/components/ui";
import { Input } from "@tonic/shared/components/ui";
import { Label } from "@tonic/shared/components/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@tonic/shared/components/ui";
import { Filter, X } from 'lucide-react';
import PropTypes from "prop-types";

/**
 * @typedef {'new'|'in_recommerce'|'available'|'sold'|'damaged'} InventoryStatus
 */

/**
 * @typedef {Object} InventoryFilters
 * @property {string} [search] - Search query for filtering items
 * @property {string} [locationId] - ID of the location to filter by
 * @property {InventoryStatus} [status] - Status to filter by
 */

/**
 * @typedef {Object} InventoryFiltersProps
 * @property {InventoryFilters} filters - Current filter values
 * @property {(filters: InventoryFilters) => void} onFilterChange - Callback when filters change
 */

const LOCATIONS = [
  { id: 'Main Warehouse', name: 'Main Warehouse' },
  { id: 'Store Front', name: 'Store Front' },
];

const STATUSES = [
  { id: 'In Stock', name: 'In Stock' },
  { id: 'Low Stock', name: 'Low Stock' },
  { id: 'Out of Stock', name: 'Out of Stock' },
];

/**
 * Component for filtering inventory items by search, location, and status
 * @param {InventoryFiltersProps} props
 * @returns {JSX.Element} The inventory filters component
 */
export function InventoryFilters({ filters, onFilterChange }) {
  const { data: locations, isLoading: locationsLoading } = useLocations();
  const [search, setSearch] = useState(filters.search || "");

  useEffect(() => {
    const timer = setTimeout(() => {
      onFilterChange({ ...filters, search });
    }, 500);

    return () => clearTimeout(timer);
  }, [search, filters, onFilterChange]);

  /**
   * Handles changes to the location filter
   * @param {string} locationId - The selected location ID
   */
  const handleLocationChange = (locationId) => {
    onFilterChange({
      ...filters,
      locationId: locationId === 'all' ? null : locationId,
    });
  };

  /**
   * Handles changes to the status filter
   * @param {InventoryStatus} status - The selected status
   */
  const handleStatusChange = (status) => {
    onFilterChange({
      ...filters,
      status: status === 'all' ? null : status,
    });
  };

  /**
   * Clears all active filters
   */
  const handleClearFilters = () => {
    setSearch("");
    onFilterChange({
      search: "",
      locationId: null,
      status: null
    });
  };

  const hasActiveFilters = filters.locationId || filters.status;

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
      <div className="flex-1">
        <Input
          placeholder="Search items..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
      </div>
      <div className="flex flex-1 items-center gap-4">
        <div className="w-[180px]">
          <Label>Location</Label>
          <Select
            value={filters.locationId || 'all'}
            onValueChange={handleLocationChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {LOCATIONS.map((location) => (
                <SelectItem key={location.id} value={location.id}>
                  {location.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-[180px]">
          <Label>Status</Label>
          <Select 
            value={filters.status || 'all'}
            onValueChange={handleStatusChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.map((status) => (
                <SelectItem key={status.id} value={status.id}>
                  {status.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {hasActiveFilters && (
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              className="h-8 px-2"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

InventoryFilters.propTypes = {
  filters: PropTypes.shape({
    search: PropTypes.string,
    locationId: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  onFilterChange: PropTypes.func.isRequired,
}; 